import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@bookdental/api";
import { ROLE_LABEL } from "@/components/layout/OrganizationSwitcherPartes";

export type OrgDoPainel = inferRouterOutputs<AppRouter>["painelDasOrganizacoes"]["resumo"][number];
export type Atencao = "atrasados" | "aceite" | "hoje" | "vencido" | "naoLidas";
export type Grupo = { chave: "clinicas" | "laboratorios" | "ambos" | "radiologia"; titulo: string; orgs: OrgDoPainel[] };

const ehClinica = (o: OrgDoPainel) => !!o.clinica;
const ehLab = (o: OrgDoPainel) => !!o.laboratorio;

export const papelDe = (o: OrgDoPainel) => (o.pessoal ? "Pessoal" : (ROLE_LABEL[o.papel] ?? o.papel));

/** Quanto de cada sinal de atenção uma organização tem (nulo e sem permissão contam zero). */
export function atencaoDe(o: OrgDoPainel, naoLidas: number): Record<Atencao, number> {
  return {
    atrasados: (o.clinica?.pedidosAtrasados ?? 0) + (o.laboratorio?.atrasados ?? 0),
    aceite: o.laboratorio?.aguardandoAceite ?? 0,
    hoje: o.clinica?.consultasHoje ?? 0,
    vencido: o.laboratorio?.vencidoCents ?? 0,
    naoLidas,
  };
}

const peso = (a: Record<Atencao, number>) => a.atrasados * 3 + a.aceite * 2 + (a.vencido > 0 ? 2 : 0);

/** Grupos por tipo; dentro de cada um, a ativa primeiro, depois quem pede mais atenção, depois o nome. */
export function agrupar(orgs: OrgDoPainel[], ativaId: string | undefined, naoLidas: Record<string, number>): Grupo[] {
  const ordenar = (lista: OrgDoPainel[]) =>
    [...lista].sort(
      (a, b) =>
        Number(b.id === ativaId) - Number(a.id === ativaId) ||
        peso(atencaoDe(b, naoLidas[b.id] ?? 0)) - peso(atencaoDe(a, naoLidas[a.id] ?? 0)) ||
        a.nome.localeCompare(b.nome, "pt-BR"),
    );
  const grupos: Grupo[] = [
    { chave: "clinicas", titulo: "Clínicas", orgs: orgs.filter((o) => ehClinica(o) && !ehLab(o)) },
    { chave: "laboratorios", titulo: "Laboratórios", orgs: orgs.filter((o) => ehLab(o) && !ehClinica(o)) },
    { chave: "ambos", titulo: "Clínica e laboratório", orgs: orgs.filter((o) => ehClinica(o) && ehLab(o)) },
    { chave: "radiologia", titulo: "Radiologia", orgs: orgs.filter((o) => !ehClinica(o) && !ehLab(o)) },
  ];
  return grupos.filter((g) => g.orgs.length).map((g) => ({ ...g, orgs: ordenar(g.orgs) }));
}
